import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const PCPClaimsFAQPage = () => {
  const faqs = [
    {
      question: "What is PCP car finance?",
      answer: "PCP (Personal Contract Purchase) is a type of car finance where you pay a deposit and monthly payments over a fixed term, with a large 'balloon payment' at the end if you want to own the car. It's one of the most popular ways to finance a vehicle in the UK."
    },
    {
      question: "Why might my PCP finance have been mis-sold?",
      answer: "Your PCP finance may have been mis-sold if you were not told about Discretionary Commission Arrangements (DCAs). Under these arrangements, dealers could increase your interest rate to earn more commission, without disclosing this to you. The FCA banned DCAs in January 2021."
    },
    {
      question: "What are Discretionary Commission Arrangements (DCAs)?",
      answer: "DCAs were agreements between car dealers and lenders that allowed dealers to set or influence the interest rate on your finance. The higher the rate they set, the more commission they received. This created a conflict of interest that was often not disclosed to consumers."
    },
    {
      question: "What period does the FCA investigation cover?",
      answer: "The FCA's investigation focuses on motor finance agreements entered into between April 2007 and January 2021. If your finance falls within this period, you may be affected."
    },
    {
      question: "How much compensation could I receive?",
      answer: "The FCA has estimated average compensation of around £830 per affected agreement, though individual amounts vary significantly based on factors such as the amount borrowed, the interest rate, and how long you had the agreement. We cannot guarantee any specific outcome."
    },
    {
      question: "Does HP (Hire Purchase) finance qualify too?",
      answer: "Yes, HP (Hire Purchase) agreements are also covered by the FCA investigation. DCAs were used across various types of motor finance, including PCP, HP, and conditional sale agreements."
    },
    {
      question: "Is there a deadline to make a claim?",
      answer: "The FCA has paused the deadline for motor finance complaints while their review is ongoing. However, it's advisable to have your case assessed early so you're prepared when the process is finalised."
    },
    {
      question: "Do I need my original finance documents?",
      answer: "While original documents are helpful, they are not essential. Blue Lion Law can request the necessary information from lenders as part of the claims process."
    },
    {
      question: "What if I've finished paying off my finance?",
      answer: "You may still be able to claim even if you have finished paying off your finance agreement or no longer own the vehicle. The claim relates to how the finance was sold, not your current situation."
    },
    {
      question: "How long does the claims process take?",
      answer: "Timescales vary depending on the complexity of the case and the responsiveness of the parties involved. The FCA's ongoing review may also affect timelines. We will keep you informed throughout the process."
    },
    {
      question: "What does 'no win, no fee' mean?",
      answer: "No win, no fee means that if your claim is unsuccessful, you won't be charged for our services. If your claim succeeds, our fee will be a percentage of the compensation recovered. Full terms will be provided before you proceed."
    },
    {
      question: "Can I claim for GAP insurance as well?",
      answer: "Yes, GAP insurance sold alongside your car finance may also have been mis-sold. The FCA has separate concerns about the GAP insurance market, including high commission rates and poor value for money. We can assess both types of claim."
    }
  ];

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* FAQ Schema Script */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">
                  Blue Lion Law
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                PCP Claims FAQ
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Common questions about PCP car finance mis-selling and the claims process
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
                  >
                    <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* CTA */}
              <div className="mt-12 p-8 rounded-2xl bg-muted/50 border border-border text-center">
                <h2 className="font-heading text-xl font-bold text-foreground mb-4">
                  Have More Questions?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Our team can assess your individual circumstances and answer any specific questions you may have.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="gold" size="lg" asChild>
                    <a href="/pcp-car-finance-claims">
                      Start Your Assessment
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <a href="tel:02081530033">
                      Call 0208 153 0033
                    </a>
                  </Button>
                </div>
              </div>

              {/* Related Links */}
              <div className="mt-12">
                <h3 className="font-heading text-lg font-bold text-foreground mb-6">
                  Related Articles
                </h3>
                <div className="grid gap-4">
                  <a 
                    href="/pcp/what-is-pcp-car-finance"
                    className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-foreground">What is PCP Car Finance?</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                  <a 
                    href="/pcp/how-pcp-is-mis-sold"
                    className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-foreground">How PCP is Mis-sold</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                  <a 
                    href="/gap-insurance-claims"
                    className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-foreground">GAP Insurance Claims</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PCPClaimsFAQPage;
